/**
 * Syncs the ShowTour Recoil atom into reactour's isOpen. In landscape this
 * lives inside TourControl (always mounted in the TopBar); portrait's Tour
 * button is inside the transient More sheet, so the sync needs a component
 * that never unmounts — this one, rendered directly under TourProvider.
 * @module TourStateBridge
 * @category Functional Components
 */
import { useTour } from "@reactour/tour";
import { useEffect } from "react";
import { useRecoilState, useRecoilValue } from "recoil";
import { useClosePanelsForTour } from "../../hooks/useClosePanelsForTour";
import { DemoMode, PendingTourStepAtom, ShowTour } from "../../state/uiAtoms";

export function TourStateBridge() {
	const [showTour, setShowTour] = useRecoilState(ShowTour);
	const demoMode = useRecoilValue(DemoMode);
	const [pendingTourStep, setPendingTourStep] =
		useRecoilState(PendingTourStepAtom);
	const closePanelsForTour = useClosePanelsForTour();
	const { setIsOpen, setCurrentStep } = useTour();

	useEffect(() => {
		setShowTour(demoMode);
	}, [demoMode, setShowTour]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: pendingTourStep is consumed, not watched
	useEffect(() => {
		if (!showTour) {
			setIsOpen(false);
			return;
		}
		// cold start (login tour button, ?enterDemo=true): the editor is still
		// mounting behind Suspense. If the tour opens before #tab-chat exists,
		// reactour measures nothing and the step highlights never appear — it
		// doesn't re-measure when the element turns up later. Wait for the tab
		// bar (up to ~5s), then open.
		let cancelled = false;
		let attempts = 0;
		const open = () => {
			// no modal may sit on top of the tour (first-signup arrives with
			// the presentations panel open from the /unset route)
			closePanelsForTour();
			setIsOpen(true);
			setCurrentStep(pendingTourStep ?? 0);
			if (pendingTourStep !== null) setPendingTourStep(null);
		};
		const tryOpen = () => {
			if (cancelled) return;
			if (document.querySelector("#tab-chat") || attempts > 100) {
				open();
			} else {
				attempts++;
				setTimeout(tryOpen, 50);
			}
		};
		tryOpen();
		return () => {
			cancelled = true;
		};
	}, [showTour, setIsOpen, setCurrentStep, setPendingTourStep]);

	return <></>;
}
